import React from 'react';

import css from './RecommendedCard.module.css';

/**
 * Mode B fallback card — self-contained and themeable via CSS variables.
 *
 * Rendered from the raw API response shape (TASK157 subtask 2.6 / D4b):
 *   { listingId, title, priceAmount, priceCurrency, similarity, imageUrls }
 *   imageUrls: Array<{ originalUrl, azureBlobUrl, variants }>
 *
 * Uses imageUrls[0]: prefers the public Sharetribe CDN (`originalUrl` /
 * `variants.default.url`), falling back to empavision's `azureBlobUrl` only as a
 * last resort (that container may be private, e.g. sharetribe-dev-*). Mode A (the
 * SDK card) uses the Sharetribe variant map directly; Mode B requests the base URL.
 *
 * Click-through is via `listingUrl(listing)` — a host-supplied function that
 * maps a raw API result to a URL string, e.g.:
 *   listingUrl={listing => `/l/${listing.listingId}`}
 * Mandatory for Mode B so the package never hardcodes URL patterns.
 *
 * Themeable without forking: override `--emp-card-*` CSS variables on a
 * parent. See RecommendedCard.module.css for the full variable list.
 *
 * @param {{ listing: object, listingUrl: (listing: object) => string }} props
 */
export const RecommendedCard = ({ listing, listingUrl }) => {
  if (!listing) {
    return null;
  }

  const { title, priceAmount, priceCurrency, imageUrls } = listing;

  // Resolve image: prefer the public Sharetribe CDN (originalUrl / imgix variant).
  // azureBlobUrl is empavision's internal copy and may live in a private container
  // (e.g. sharetribe-dev-*, which returns 409 to anonymous reads), so it is only a
  // last-resort fallback for a public <img>.
  const firstImage = Array.isArray(imageUrls) && imageUrls.length > 0 ? imageUrls[0] : null;
  const imageSrc = firstImage
    ? firstImage.originalUrl ||
      (firstImage.variants && firstImage.variants.default && firstImage.variants.default.url) ||
      firstImage.azureBlobUrl ||
      null
    : null;

  // Format price — simple locale format, no i18n lib dependency.
  const formattedPrice =
    priceAmount !== undefined && priceAmount !== null && priceCurrency
      ? new Intl.NumberFormat(undefined, { style: 'currency', currency: priceCurrency }).format(
          priceAmount / 100
        )
      : null;

  const href = typeof listingUrl === 'function' ? listingUrl(listing) : '#';

  return (
    <a href={href} className={css.card}>
      <div className={css.imageWrapper}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={title || ''}
            className={css.image}
            loading="lazy"
          />
        ) : (
          <div className={css.imagePlaceholder} aria-hidden="true">
            {/* intentionally blank: no broken-image icon */}
          </div>
        )}
      </div>
      <div className={css.body}>
        {title ? <p className={css.title}>{title}</p> : null}
        {formattedPrice ? <p className={css.price}>{formattedPrice}</p> : null}
      </div>
    </a>
  );
};
